/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================
*/

import { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "pages/context/AuthContext";
import { AlertProvider } from "pages/context/AlertContext";
import { subscreverPush } from "pages/utils/webPush";

import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import GlobalStyles from "@mui/material/GlobalStyles";

import MDBox from "components/MDBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

import theme from "assets/theme";
import themeDark from "assets/theme-dark";

import routes from "routes";

import ForcePasswordChange from "pages/auth/ForcePasswordChange";
import FichaProduto from "pages/stocks/FichaProduto";

import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
  getDirectionalGradient,
  getNavbarBgColor,
  getSidenavBgColor,
  getAppBgColor,
} from "context";

import brandWhite from "assets/images/logo-ct_branco.png";
import brandDark from "assets/images/logo-ct-dark_branco.png";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const { isAuthenticated, loading, userData } = useAuth();

  const {
    miniSidenav,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    uiOverrides,
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();
  const isAuthRoute = pathname.startsWith("/auth");
  const isProdutoRoute = pathname.startsWith("/produto");
  const hideAppChrome = isAuthRoute || isProdutoRoute;

  // efeitos UI
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    if (!isAuthenticated) return;

    const publicKey =
      "BBZKJAtA3BkweWrBRfUbZ1VihqC4BFhmuTIsvIz0v8mTuVouViPSB7tTtZ89hWSDcVnVlkGdeJ9MXwvWPZrBVb0";
    const token = localStorage.getItem("token");

    if (!token) return;

    subscreverPush(publicKey, token, userData?.id);
  }, [isAuthenticated, userData?.id]);

  useEffect(() => {
    // scroll ao topo em cada navegação
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) return getRoutes(route.collapse);
      if (route.route)
        return (
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.key || route.route}
          />
        );
      return null;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  const shouldShowConfigurator = !uiOverrides?.hideConfigurator;
  const step = uiOverrides?.gradientStep ?? 26;

  const navbarBg = getNavbarBgColor(controller);
  const sidenavBg = getSidenavBgColor(controller);

  // fundo global da app
  const appBg = getAppBgColor(controller);
  const appStep = uiOverrides?.appBgStep ?? 0; // 0 = sólido; >0 = gradiente
  const appDir = uiOverrides?.appBgDir ?? "tb";

  const GlobalBgStyles = (
    <GlobalStyles
      styles={(theme) => {
        const base = {
          "html, body, #app": {
            background: appBg
              ? appStep > 0
                ? getDirectionalGradient(theme, appBg, appDir, appStep)
                : appBg
              : "transparent",
          },
          ".MuiAppBar-root": {
            backgroundImage: `${getDirectionalGradient(theme, navbarBg, "lr", step)} !important`,
            backgroundColor: `transparent !important`,
          },
          ".MuiDrawer-paper": {
            backgroundImage: `${getDirectionalGradient(theme, sidenavBg, "tb", step)} !important`,
          },
        };

        // Só limpamos backgrounds dos wrappers fora de /auth
        if (!hideAppChrome) {
          base["#app > .MuiBox-root"] = {
            background: "transparent !important",
            backgroundImage: "none !important",
          };
          base["#app > .MuiBox-root > .MuiBox-root"] = {
            background: "transparent !important",
            backgroundImage: "none !important",
          };
        }

        return base;
      }}
    />
  );

  const RoutesElement = (
    <Routes>
      <Route path="/produto/:refmaterial" element={<FichaProduto />} />
      {getRoutes(routes)}
      <Route
        path="*"
        element={<Navigate to={isAuthenticated ? "/dashboard" : "/auth/login"} replace />}
      />
    </Routes>
  );

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {GlobalBgStyles}

      <AlertProvider>
        <MDBox sx={{ minHeight: "100vh", background: "transparent !important" }}>
          {layout === "dashboard" && !hideAppChrome && !loading && isAuthenticated && (
            <>
              <Sidenav
                color={sidenavColor}
                brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                brandName="JF Gest"
                routes={routes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
              {shouldShowConfigurator && <Configurator />}
              {shouldShowConfigurator && configsButton}
            </>
          )}

          {layout === "vr" && shouldShowConfigurator && !hideAppChrome && <Configurator />}

          {/* Enforcer global: se a pwd for provisória, bloqueia a app e obriga a troca */}
          <ForcePasswordChange />

          {RoutesElement}
        </MDBox>
      </AlertProvider>
    </ThemeProvider>
  );
}
